import { Controller } from '@hotwired/stimulus'
import { DirectUpload } from '@rails/activestorage'
import { post } from '@rails/request.js'
import { buildUploadChip as buildUploadChipFromLibrary, setChipProgress as setChipProgressFromLibrary } from '../lib/upload_chip'

const DRAG_ACTIVE_CLASSES = ['!border-brand-accent', '!ring-1', '!ring-inset', '!ring-brand-accent', '!bg-brand-accent/30']

function buildUploadChipForRuntime(options) {
  if (typeof buildUploadChipFromLibrary === 'function') {
    return buildUploadChipFromLibrary(options)
  }

  const chip = document.createElement('span')
  chip.className = 'badge inline-flex items-center gap-2 max-w-full min-w-0 overflow-hidden'
  chip.id = options.id

  const name = document.createElement('span')
  name.className = 'truncate min-w-0 file-name'
  name.textContent = options.state === 'uploading' ? `Uploading ${options.filename}...` : options.filename
  chip.appendChild(name)

  const meta = document.createElement('span')
  meta.className = 'file-percent text-ink-faint text-xs shrink-0'
  meta.textContent = options.state === 'uploading' ? '0%' : ''
  chip.appendChild(meta)

  return chip
}

function setChipProgressForRuntime(chip, percentage) {
  if (typeof setChipProgressFromLibrary === 'function') {
    setChipProgressFromLibrary(chip, percentage)
    return
  }

  if (!chip) return

  const progress = chip.querySelector('.file-percent')
  if (progress) progress.textContent = `${Math.round(Math.max(0, Math.min(100, percentage)))}%`
}

class Upload {
  constructor(file, endpoint) {
    this.directUpload = new DirectUpload(
      file,
      '/rails/active_storage/direct_uploads',
      this
    )
    this.endpoint = endpoint
  }

  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener('progress', (event) =>
      this.updateProgress(event)
    )
  }

  updateProgress(event) {
    if (!event.total) return

    // The chip can be gone already — a finished upload removes it, and the last
    // progress event can land after that. This used to dereference null and
    // throw, which took the rest of the upload's callbacks with it.
    setChipProgressForRuntime(this.chip(), (event.loaded / event.total) * 100)
  }

  chip() {
    return document.getElementById(`upload_${this.directUpload.id}`)
  }

  insertUpload() {
    // remove #empty_paperwork_events
    const emptyUploads = document.querySelector('#empty_upload_lines')
    if (emptyUploads) {
      emptyUploads.remove()
    }

    // remove .remove-on-upload
    const removeOnUpload = document.querySelectorAll('.remove-on-upload')
    removeOnUpload.forEach((el) => {
      el.remove()
    })

    const uploadList = document.querySelector('#upload_lines')
    if (!uploadList) return

    uploadList.appendChild(buildUploadChipForRuntime({
      id: `upload_${this.directUpload.id}`,
      filename: this.directUpload.file.name,
      byteSize: this.directUpload.file.size,
      state: 'uploading'
    }))

    const paperworkEventMessage = document.querySelector("#paperwork_event_message");
    if (paperworkEventMessage) {
      paperworkEventMessage.focus();
    }
  }

  process() {
    this.insertUpload();

    this.directUpload.create((error, blob) => {
      if (error) {
        this.showFailure()
        this.controller?.uploadFailed(this)
      } else {
        const uploadData = {
          upload_data: {
            filename: blob.filename,
            byte_size: blob.byte_size,
            checksum: blob.checksum,
            signed_blob_id: blob.signed_id,
          },
        }

        const event = new CustomEvent("new_completed_upload", {
          detail: uploadData.upload_data,
        });
        document.querySelector("[data-controller='completed-event-uploads']").dispatchEvent(event);
        const uploadElement = document.querySelector(`#upload_${this.directUpload.id}`);
        if (uploadElement) {
          uploadElement.remove();
        }
        
        // Notify controller that this upload is complete
        if (this.controller) {
          this.controller.uploadCompleted()
        }
        // post(this.endpoint, {
        //   body: JSON.stringify(uploadData),
        //   contentType: 'application/json',
        //   responseKind: 'json',
        // }).then(() => {
        //   // Remove the upload element after successful upload
        //   const uploadElement = document.querySelector(`#upload_${this.directUpload.id}`);
        //   if (uploadElement) {
        //     uploadElement.remove();
        //   }
        // }).catch((error) => {
        //   console.error('Upload failed:', error);
        //   // Optionally, update the UI to show the error
        // });
      }
    })
  }

  showFailure() {
    const element = document.querySelector(`#upload_${this.directUpload.id}`)
    if (!element) return

    element.classList.add('badge-error')
    element.setAttribute('role', 'alert')
    element.querySelector('.file-name').textContent = `Upload failed: ${this.directUpload.file.name}`
    element.querySelector('.file-percent')?.remove()
    for (const [label, action] of [['Retry', () => this.retry()], ['Remove', () => this.removeFailure()]]) {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'underline shrink-0'
      button.textContent = label
      button.addEventListener('click', action)
      element.appendChild(button)
    }
  }

  removeFailure() {
    document.querySelector(`#upload_${this.directUpload.id}`)?.remove()
    this.controller?.resolveUploadFailure(this)
  }

  retry() {
    // Start a new storage request; a failed DirectUpload instance is not reusable.
    const next = new Upload(this.directUpload.file, this.endpoint)
    next.controller = this.controller
    if (this.controller) this.controller.activeUploads += 1
    this.removeFailure()
    next.process()
  }
}

export default class extends Controller {
  static targets = ['fileInput', "submitButton"]
  static values = { endpoint: String }
  
  initialize() {
    this.activeUploads = 0
    this.failedUploads = new Set()
    this.boundSubmit = this.preventIncompleteSubmission.bind(this)
    this.dragDepth = 0
    this.boundDragEnter = this.dragEnter.bind(this)
    this.boundDragOver = this.dragOver.bind(this)
    this.boundDragLeave = this.dragLeave.bind(this)
    this.boundDrop = this.drop.bind(this)
  }

  connect() {
    this.form = this.element.closest('form') || this.element.querySelector('form')
    this.form?.addEventListener('submit', this.boundSubmit, true)
    this.element.addEventListener('dragenter', this.boundDragEnter)
    this.element.addEventListener('dragover', this.boundDragOver)
    this.element.addEventListener('dragleave', this.boundDragLeave)
    this.element.addEventListener('drop', this.boundDrop)
  }

  disconnect() {
    this.form?.removeEventListener('submit', this.boundSubmit, true)
    this.element.removeEventListener('dragenter', this.boundDragEnter)
    this.element.removeEventListener('dragover', this.boundDragOver)
    this.element.removeEventListener('dragleave', this.boundDragLeave)
    this.element.removeEventListener('drop', this.boundDrop)
    this.dragDepth = 0
    this.clearDragState()
  }

  hasFiles(event) {
    const types = event?.dataTransfer?.types
    return types ? Array.from(types).includes('Files') : false
  }

  clearDragState() {
    delete this.element.dataset.drag
  }

  applyDragState() {
    this.element.dataset.drag = 'active'
  }

  preventDragDefaults(event) {
    event.preventDefault()
    event.stopPropagation()
  }

  dragEnter(event) {
    if (!this.hasFiles(event)) return

    this.dragDepth += 1
    this.applyDragState()
    this.preventDragDefaults(event)
  }

  dragLeave(event) {
    if (this.dragDepth > 0) {
      this.dragDepth -= 1
    }

    if (this.dragDepth === 0) {
      this.clearDragState()
    }

    this.preventDragDefaults(event)
  }

  dragOver(event) {
    if (!this.hasFiles(event)) return

    this.applyDragState()
    this.preventDragDefaults(event)
  }

  drop(event) {
    this.dragDepth = 0
    this.clearDragState()
    this.preventDragDefaults(event)
  }

  trigger() {
    console.log('triggering file input')
    this.fileInputTarget.click()
  }

  acceptFiles(event) {
    console.log('accepting files', event)
    event.preventDefault()
    this.dragDepth = 0
    this.clearDragState()
    const files = event.dataTransfer
      ? event.dataTransfer.files
      : event.target.files
    
    if (files.length > 0) {
      this.disableSubmitButton()
      this.activeUploads += files.length
    }
    
    ;[...files].forEach((f) => {
      const upload = new Upload(f, this.endpointValue)
      upload.controller = this
      upload.process()
    })
  }
  
  disableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }
  }
  
  enableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
    }
  }
  
  uploadCompleted() {
    this.activeUploads = Math.max(0, this.activeUploads - 1)
    this.updateSubmitState()
  }

  uploadFailed(upload) {
    this.failedUploads.add(upload)
    this.uploadCompleted()
  }

  resolveUploadFailure(upload) {
    this.failedUploads.delete(upload)
    this.updateSubmitState()
  }

  updateSubmitState() {
    if (this.activeUploads > 0 || this.failedUploads.size > 0) this.disableSubmitButton()
    else this.enableSubmitButton()
  }

  preventIncompleteSubmission(event) {
    if (this.activeUploads === 0 && this.failedUploads.size === 0) return
    event.preventDefault()
    event.stopImmediatePropagation()
  }
}
